import { initializeApp, getApps, type FirebaseApp, type FirebaseOptions } from "firebase/app";

// Shared across iam, storage, and database so multiple mksys projects
// pointing at the same Firebase project reuse one FirebaseApp instead of
// racing `initializeApp` and hitting `duplicate-app`.
const apps = new Map<string, { app: FirebaseApp; fingerprint: string }>();

// Serialize the config fields that would change app behaviour if swapped
// (apiKey, authDomain, appId, storageBucket, projectId, messagingSenderId,
// measurementId). A cache hit with a different fingerprint means two
// FirebaseConfigs share projectId but disagree on, say, apiKey — which
// would silently serve the first caller's credentials to everyone.
function fingerprint(o: FirebaseOptions): string {
    return JSON.stringify([o.projectId, o.apiKey, o.authDomain, o.appId, o.storageBucket, o.messagingSenderId, o.measurementId]);
}

export function getFirebaseApp(options: FirebaseOptions): FirebaseApp {
    const name = `app-${options.projectId}`;
    const fp = fingerprint(options);
    const cached = apps.get(name);
    if (cached) {
        if (cached.fingerprint !== fp) {
            console.warn(`[shared/firebase] reusing cached app '${name}' but caller's config differs — returning the first-registered config. Check that every caller passes the same FirebaseConfig for projectId=${options.projectId}.`);
        }
        return cached.app;
    }
    const existing = getApps().find(a => a.name === name);
    if (existing) {
        apps.set(name, { app: existing, fingerprint: fp });
        return existing;
    }
    const app = initializeApp(options, name);
    apps.set(name, { app, fingerprint: fp });
    return app;
}
